import React from "react";
import styled from "styled-components";

import { LibraryView } from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { app } from "js/namespaces";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import { LibraryTeamSettingsNavigation, isTeamSettingsPane } from "js/editor/PresentationLibrary/LibraryTeamSettingsNavigation";
import { LibraryTeamLibrariesNavigation } from "js/editor/PresentationLibrary/LibraryTeamLibrariesNavigation";
import {
    navigationSelectedClass,
    StyledNavigationIcon,
    StyledNavigationSideBarItem,
} from "js/editor/PresentationLibrary/libraryNavigationItems";
import { t } from "js/i18n";
import { FlexSpacer } from "js/react/components/Gap";
import UpgradeIcon from "js/Components/UpgradeIcon";
import UpgradeSection from "js/Components/UpgradeSection";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";

const StyledUpgradeSection = styled(UpgradeSection)`
    background: #000;

    #upgrade-section-header {
        color: #DDD;
    }

    #upgrade-section-body {
        color: #DDD;
    }
`;

const NavigationSidebarContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    min-height: 0;
`;

type LibraryNavigationSidebarProps = {
    libraryView: typeof LibraryView.LIBRARY | typeof LibraryView.TEMPLATES | typeof LibraryView.TEAM_RESOURCES;
    teamPane?: string;
};

function shouldShowTeamResources() {
    const isGuest = WorkspaceController.isGuest;
    const isBasic = WorkspaceController.isBasic;
    const canManageSharedSlides =
        WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedSlides =
        WorkspaceController.canUpgradeToRemoveActionRestriction(
            ActionPermissionsObject.SHARED_RESOURCES,
            PermissionActionType.MANAGE
        );

    if (isGuest || isBasic || app.integrator) {
        return false;
    }

    return canManageSharedSlides || canUpgradeToManageSharedSlides;
}

export function LibraryNavigationSidebar({ libraryView, teamPane }: LibraryNavigationSidebarProps) {
    const showTeamResources = shouldShowTeamResources();

    if (WorkspaceController.isMultiUserWorkspace && isTeamSettingsPane(teamPane)) {
        return <LibraryTeamSettingsNavigation teamPane={teamPane} />;
    }

    const isLibrary = libraryView === LibraryView.LIBRARY;
    const isTemplates = libraryView === LibraryView.TEMPLATES;

    const showUpgradeDialog = (featuredPlan: WorkspacePlanTier) => {
        ShowDialog(ChangePlanDialog, {
            featuredPlan
        });
    };

    const upgradeMessage = () => {
        let title: string | undefined;
        let body: string | undefined;
        let buttonText: string | undefined;
        let buttonOnClick: (() => void) | undefined;

        if (!WorkspaceController.isGuest && !WorkspaceController.isBasic) {
            return null;
        }

        if (WorkspaceController.isGuest) {
            title = t("You're a guest");
            body = t("As a guest in this workspace you can collaborate, but can’t create new presentations. To unlock all of Beautiful.ai’s features, create a new workspace.");
            buttonText = t("New Workspace");
            buttonOnClick = () => ShowDialog(GuestWorkspaceDialog);
        } else if (WorkspaceController.isBasic) {
            title = t("Unlock advanced features");
            body = t("Remove watermarks and access AI generation, analytics, and editable export.");
            buttonText = t("Upgrade");
            buttonOnClick = () => showUpgradeDialog(WorkspacePlanTier.PRO);
        }

        return (
            <StyledUpgradeSection
                title={title}
                body={body}
                buttonText={buttonText}
                buttonIcon={<UpgradeIcon />}
                buttonOnClick={buttonOnClick}
            />
        );
    };

    const onClickMyWork = () => {
        if (isLibrary) {
            return;
        }

        AppController.showLibrary();
    };

    const onClickTemplates = () => {
        if (isTemplates) {
            return;
        }

        // Sets as default pane for create presentation dialog
        const paneType = WorkspaceController.isMultiUserWorkspace
            ? CreatePresentationDialogPaneType.TEAM_TEMPLATES
            : CreatePresentationDialogPaneType.STARTER_TEMPLATES;

        AppController.showCreatePresentationDialog({
            pane: paneType,
        });
    };

    return (
        <NavigationSidebarContent>
            <StyledNavigationSideBarItem
                className={navigationSelectedClass(isLibrary)}
                id="my-presentations"
                data-test-id="sidebar-nav-my-presentations"
                onClick={onClickMyWork}
            >
                <StyledNavigationIcon>video_library</StyledNavigationIcon>
                {t("My Presentations")}
            </StyledNavigationSideBarItem>
            {!WorkspaceController.isGuest && (
                <StyledNavigationSideBarItem
                    className={navigationSelectedClass(isTemplates)}
                    id="library-templates"
                    data-test-id="sidebar-nav-templates"
                    onClick={onClickTemplates}
                >
                    <StyledNavigationIcon>lightbulb</StyledNavigationIcon>
                    {t("Templates")}
                </StyledNavigationSideBarItem>
            )}
            {showTeamResources && <LibraryTeamLibrariesNavigation libraryView={libraryView} teamPane={teamPane} /> }
            <FlexSpacer />
            {upgradeMessage()}
        </NavigationSidebarContent>
    );
}
